import React, { useEffect, useMemo, useState } from "react";
import { Spin, Button, Select, Switch } from "antd";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { HappyProvider } from "@ant-design/happy-work-theme";
import axios from "axios";

import { notify } from "../../components/ui";
import { PATH } from "../../redux/utils";
import {
  actionEmpresaGetById,
  actionEmpresaCreate,
  actionEmpresaUpdateById,
} from "../../redux/actions/empresas/empresas.js";
import "../../components/forms/tribunal/FormTribunal.css";

const API_BASE = PATH.endsWith("/") ? PATH : `${PATH}/`;

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const collapseSpaces = (s) =>
  String(s ?? "").normalize("NFC").replace(/\s+/g, " ").trim();

const onlyPhoneDigits = (v) => String(v || "").replace(/\D/g, "").slice(0, 15);

const normNombre = (s) =>
  String(s ?? "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const emptyRazon = () => ({ nombre: "", tiene_corresponsal: false, corresponsales_ids: [] });

export default function CrearEmpresaPage() {
  const { idEmpresa } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isEdit = Boolean(idEmpresa);
  const fromCorresponsal = location.state?.preCorresponsalId ?? null;
  const returnUrl = location.state?.returnUrl ?? null;

  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [detalle, setDetalle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({ nombre: "", razones: [] });

  const [corresponsalesCatalog, setCorresponsalesCatalog] = useState([]);
  const [rsIndex, setRsIndex] = useState({}); // normNombre -> nombre de la empresa dueña (para detectar duplicados)

  const [form, setForm] = useState({
    nombre: "",
    correo: "",
    celular: "",
    razones_sociales: fromCorresponsal
      ? [{ nombre: "", tiene_corresponsal: true, corresponsales_ids: [fromCorresponsal] }]
      : [emptyRazon()],
  });

  const handleBack = () => {
    if (returnUrl) { navigate(returnUrl); return; }
    navigate(isEdit ? `/empresas/${idEmpresa}` : "/empresas");
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}empresas/corresponsales-catalog`, { headers: authHeader() })
      .then((r) => setCorresponsalesCatalog(r.data || []))
      .catch(() => {});
  }, []);

  // Índice de razones sociales existentes (excluye la empresa actual en edición) para bloquear duplicados
  useEffect(() => {
    const currentId = isEdit ? Number(idEmpresa) : null;
    axios
      .get(`${API_BASE}empresas`, { headers: authHeader() })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : Array.isArray(r.data?.items) ? r.data.items : [];
        const idx = {};
        for (const emp of list) {
          const empId = emp.id_empresa ?? emp.id ?? null;
          if (currentId && empId === currentId) continue;
          const empNombre = emp.nombre || "";
          const rs = Array.isArray(emp.razones_sociales) ? emp.razones_sociales : [];
          for (const item of rs) {
            const nombre = item?.nombre ?? item?.code ?? "";
            if (!nombre) continue;
            const key = normNombre(nombre);
            if (!idx[key]) idx[key] = empNombre;
          }
        }
        setRsIndex(idx);
      })
      .catch(() => {});
  }, [isEdit, idEmpresa]);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingEdit(true);
        const data = await dispatch(actionEmpresaGetById(Number(idEmpresa)));
        if (!mounted) return;
        setDetalle(data || null);

        const src = data || {};
        let razones = [];
        if (Array.isArray(src.razones_sociales)) {
          razones = src.razones_sociales
            .map((r) => {
              const nombre = collapseSpaces(r?.nombre ?? r?.code ?? "");
              const corresponsales = Array.isArray(r?.corresponsales) ? r.corresponsales : [];
              const ids = corresponsales.map((c) => c.id || c.id_corresponsal).filter(Boolean);
              return { nombre, tiene_corresponsal: ids.length > 0, corresponsales_ids: ids };
            })
            .filter((x) => x.nombre);
        }
        if (!razones.length) razones = [emptyRazon()];

        setForm({
          nombre: src?.nombre ?? "",
          correo: src?.correo ?? "",
          celular: src?.celular ?? "",
          razones_sociales: razones,
        });
        setErrors({ nombre: "", razones: razones.map(() => ({ nombre: "" })) });
      } catch (e) {
        if (!mounted) return;
        notify.error("No se pudo cargar la empresa", e?.response?.data?.detail || e?.message || "Sin detalle");
        setDetalle(null);
      } finally {
        if (mounted) setLoadingEdit(false);
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, isEdit, idEmpresa]);

  const corresponsalesOptions = useMemo(
    () => corresponsalesCatalog.map((c) => ({ label: c.nombre, value: c.id_corresponsal })),
    [corresponsalesCatalog]
  );

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const setRazonField = (idx, patch) => {
    setForm((f) => {
      const razones = f.razones_sociales.slice();
      razones[idx] = { ...razones[idx], ...patch };
      return { ...f, razones_sociales: razones };
    });
  };

  const addRazon = () => {
    setForm((f) => ({ ...f, razones_sociales: [...f.razones_sociales, emptyRazon()] }));
    setErrors((e) => ({ ...e, razones: [...e.razones, { nombre: "" }] }));
  };

  const removeRazon = (idx) => {
    setForm((f) => ({ ...f, razones_sociales: f.razones_sociales.filter((_, i) => i !== idx) }));
    setErrors((e) => ({ ...e, razones: e.razones.filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    const nextErrors = { nombre: "", razones: form.razones_sociales.map(() => ({ nombre: "" })) };
    let firstInvalidId = null;

    if (!collapseSpaces(form.nombre)) {
      nextErrors.nombre = "Requerido";
      firstInvalidId = firstInvalidId || "fe-nombre";
    }

    const seenInForm = new Map();
    form.razones_sociales.forEach((r, idx) => {
      const nombre = collapseSpaces(r?.nombre || "");
      if (!nombre) {
        nextErrors.razones[idx].nombre = "Requerido";
        firstInvalidId = firstInvalidId || `fe-razon-${idx}`;
        return;
      }
      const key = normNombre(nombre);
      if (seenInForm.has(key)) {
        const firstIdx = seenInForm.get(key);
        nextErrors.razones[firstIdx].nombre = "Razón social duplicada en este formulario";
        nextErrors.razones[idx].nombre = "Razón social duplicada en este formulario";
        firstInvalidId = firstInvalidId || `fe-razon-${firstIdx}`;
      } else {
        seenInForm.set(key, idx);
        const empresaNombre = rsIndex[key];
        if (empresaNombre) {
          nextErrors.razones[idx].nombre = `Ya existe en la empresa "${empresaNombre}"`;
          firstInvalidId = firstInvalidId || `fe-razon-${idx}`;
        }
      }
    });

    setErrors(nextErrors);

    const hasErrors = !!nextErrors.nombre || nextErrors.razones.some((r) => !!r.nombre);
    if (hasErrors && firstInvalidId) {
      document.getElementById(firstInvalidId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      notify.error("Faltan campos por corregir", "Revisa los campos marcados en rojo antes de guardar.");
      return;
    }

    const rsClean = form.razones_sociales
      .map((r) => ({
        nombre: collapseSpaces(r?.nombre || ""),
        corresponsales_ids: r?.tiene_corresponsal
          ? Array.isArray(r?.corresponsales_ids) ? r.corresponsales_ids : []
          : [],
      }))
      .filter((r) => !!r.nombre);

    const payload = {
      nombre: collapseSpaces(form.nombre),
      correo: collapseSpaces(form.correo) || null,
      celular: onlyPhoneDigits(form.celular) || null,
      razones_sociales: rsClean,
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        await dispatch(actionEmpresaUpdateById(Number(idEmpresa), payload));
        notify.success("Empresa actualizada");
        handleBack();
      } else {
        const res = await dispatch(actionEmpresaCreate(payload));
        const newId = res?.id || res?.data?.id || res?.id_empresa;
        notify.success("Empresa creada");
        if (returnUrl) navigate(returnUrl);
        else navigate(newId ? `/empresas/${newId}` : "/empresas");
      }
    } catch (e) {
      notify.error(e?.response?.data?.detail || e?.message || "No se pudo guardar la empresa.");
    } finally {
      setSubmitting(false);
    }
  };

  const ready = !isEdit || !!detalle;

  return (
    <HappyProvider>
      <div className="oa-exp-page">
        {/* ── TOPBAR ─────────────────────────────────────────────────── */}
        <section className="oa-exp-topbar">
          <div className="oa-exp-topbar-left">
            <div className="oa-exp-title">{isEdit ? "Editando Empresa" : "Creando Empresa"}</div>
            <div className="oa-exp-subtitle">Directorio de Empresas</div>
          </div>
          <div className="oa-exp-topbar-right">
            <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={handleBack}>
              Cancelar
            </Button>
            <Button
              type="primary"
              className="oa-exp-btn"
              disabled={submitting}
              loading={submitting}
              onClick={handleSubmit}
              icon={
                <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                  <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                </svg>
              }
            >
              {isEdit ? "Guardar cambios" : "Crear empresa"}
            </Button>
          </div>
        </section>

        {/* ── BODY ───────────────────────────────────────────────────── */}
        <main className="oa-exp-main">
          {!ready ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <section className="oa-card">
                <div className="oa-card-head">
                  <h2 className="oa-card-title">
                    <span className="oa-card-icon">
                      <i className="fa-solid fa-building" />
                    </span>
                    DATOS DE LA EMPRESA
                  </h2>
                </div>

                <div className="oa-card-body oa-grid-3">
                  <div className="oa-field" id="fe-nombre">
                    <label className="oa-label">
                      <span className="oa-req">*</span> Nombre comercial
                    </label>
                    <input
                      className={`oa-input ${errors.nombre ? "oa-input-error" : ""}`}
                      type="text"
                      placeholder="Ej. Nombre Comercial"
                      maxLength={255}
                      value={form.nombre}
                      onChange={(e) => setField("nombre", e.target.value)}
                    />
                    {errors.nombre && <div className="oa-error">{errors.nombre}</div>}
                  </div>

                  <div className="oa-field">
                    <label className="oa-label">Correo</label>
                    <input
                      className="oa-input"
                      type="email"
                      placeholder="correo@dominio.com"
                      maxLength={225}
                      value={form.correo}
                      onChange={(e) => setField("correo", e.target.value)}
                    />
                  </div>

                  <div className="oa-field">
                    <label className="oa-label">Celular</label>
                    <input
                      className="oa-input"
                      type="text"
                      placeholder="Ej. 6531234567"
                      maxLength={15}
                      value={form.celular}
                      onChange={(e) => setField("celular", onlyPhoneDigits(e.target.value))}
                    />
                  </div>
                </div>
              </section>

              <section className="oa-card">
                <div className="oa-card-head oa-head-split">
                  <h2 className="oa-card-title">
                    <span className="oa-card-icon">
                      <i className="fa-solid fa-sitemap" />
                    </span>
                    RAZONES SOCIALES
                  </h2>
                  <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={addRazon} icon={<i className="fa-solid fa-plus" />}>
                    Agregar
                  </Button>
                </div>

                <div className="oa-card-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {form.razones_sociales.map((r, idx) => (
                    <div
                      key={idx}
                      style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, background: "#f8fafc" }}
                    >
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <div className="oa-field" style={{ flex: 1 }} id={`fe-razon-${idx}`}>
                          <label className="oa-label">
                            <span className="oa-req">*</span> Razón social
                          </label>
                          <input
                            className={`oa-input ${errors.razones[idx]?.nombre ? "oa-input-error" : ""}`}
                            type="text"
                            placeholder="Razón social (Ej. SA DE CV)"
                            value={r.nombre}
                            onChange={(e) => setRazonField(idx, { nombre: e.target.value })}
                          />
                          {errors.razones[idx]?.nombre && (
                            <div className="oa-error">{errors.razones[idx]?.nombre}</div>
                          )}
                        </div>
                        <Button
                          danger
                          className="oa-exp-btn"
                          onClick={() => removeRazon(idx)}
                          disabled={form.razones_sociales.length <= 1}
                          style={{ marginTop: 20 }}
                        >
                          Quitar
                        </Button>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                        <Switch
                          size="small"
                          checked={r.tiene_corresponsal}
                          onChange={(checked) => setRazonField(idx, { tiene_corresponsal: checked })}
                        />
                        <span className="oa-label" style={{ textTransform: "none", fontSize: 12.5 }}>
                          Tiene corresponsal
                        </span>
                      </div>

                      {r.tiene_corresponsal && (
                        <div className="oa-field" style={{ marginTop: 10 }}>
                          <label className="oa-label">Corresponsales</label>
                          <Select
                            mode="multiple"
                            value={r.corresponsales_ids}
                            onChange={(ids) => setRazonField(idx, { corresponsales_ids: ids })}
                            options={corresponsalesOptions}
                            style={{ width: "100%" }}
                            placeholder="— Seleccionar despacho —"
                            optionFilterProp="label"
                            showSearch
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </main>
      </div>
    </HappyProvider>
  );
}
